import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal, ViewEncapsulation} from '@angular/core';

import {Subscription} from 'rxjs';
import {LoaderService} from '../../../../services/loader.service';
import {ToolsService} from '../../../../services/tools.service';
import {TaxonomyEnum} from '../../../../enums/WPEnums';
import {WPTag} from '../../../../types/WPTag';
import {MetaTag} from '../../../../types/MetaTag';
import {UrlBuilderService} from '../../../../services/url-builder.service';
import {SeoMetaService} from '../../../../services/seo-meta.service';
import {WpService} from '../../../../services/wp.service';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Breadcrumb} from '../../../../types/Breadcrumb';
import {RoutesEnums, RoutesNamesEnum} from '../../../../enums/RoutesEnums';
import {finalize} from 'rxjs/operators';
import {GuideUrlResolverService} from '../../services/guide-url-resolver.service';
import {LoaderQueueItem} from '../../../../types/LoaderQueueItem';
import {WPPostGuide} from '../../../../types/WPPostGuide';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {PageViewsGa4ResolverService} from '../../../../services/page-views-ga4-resolver.service';
import {DataLayerParamsPageViewGA4} from '../../../../types/DataLayerParamsPageViewGA4';
import {StaticGuideService} from '../../../../services/static-guide.service';
import {HttpResponse} from '@angular/common/http';

@Component({
  selector: 'app-guide-taxonomy-list',
  templateUrl: './guide-taxonomy-list.component.html',
  styleUrls: ['./guide-taxonomy-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuideTaxonomyListComponent implements OnInit, OnDestroy {
  public guidePosts = signal<WPPostGuide[]>([]);
  public tag = signal<WPTag>(null);
  public isMoreBtnVisible = signal<boolean>(false);
  public breadcrumbs = signal<Breadcrumb[]>([]);
  public page = signal<number>(1);
  public totalPages = signal<number>(1);
  private pageTitle: string;
  private subscriptions: Subscription[] = [];
  private loadedPage: number = null;

  constructor(
    public urlResolver: GuideUrlResolverService,
    private loaderService: LoaderService,
    private tools: ToolsService,
    private route: ActivatedRoute,
    private router: Router,
    private urlBuilder: UrlBuilderService,
    private metaService: SeoMetaService,
    private wpService: WpService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private guideService: StaticGuideService,
  ) {}

  public ngOnInit() {
    this.loadedPage = null;
    this.initUrlResolverAndUpdateState();
    this.page.set(1);
    this.guidePosts.set([]);
    this.updateTagData();

    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.initUrlResolverAndUpdateState();
          this.page.set(1);
          this.guidePosts.set([]);
          this.updateTagData();
        }
      }),
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public loadMore(): void {
    this.page.update((prev) => prev + 1);
    this.updateGuides();
  }

  private onPuffsLoaded(puffs: WPPostGuide[]): void {
    const customGa4Params: DataLayerParamsPageViewGA4 = this.wpService.createDataLayerGa4CustomParamsForItemsList(
      puffs,
      DLPageTypeEnum.GUIDE_PAGE,
      this.pageTitle,
    );
    this.pageViewGa4Resolver.trackPageView(customGa4Params);
  }

  private initUrlResolverAndUpdateState(): void {
    this.urlResolver.cleanUpUrl(this.route.snapshot);
    this.urlResolver.resetState();
    this.urlResolver.processRouteQueryParams(this.route.snapshot.queryParams);
    this.urlResolver.processRouteParams(this.route.snapshot.params);
  }

  private updateTagData(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    this.subscriptions.push(
      this.wpService
        .GetTaxonomyItemBySlug(TaxonomyEnum.GUIDE_TAG, this.urlResolver.tagName)
        .pipe(
          finalize(() => {
            this.loaderService.removeFromQueue(queryId);
          }),
        )
        .subscribe(
          (result) => this.processTag(result[0]),
          (error) => {
            console.error(error);
          },
        ),
    );
  }

  private processTag(result: WPTag) {
    this.tag.set(result);
    this.buildBreadcrumbs();
    this.updateMetaTags();
    this.updateGuides();
  }

  private buildBreadcrumbs() {
    const breadcrumbs: Breadcrumb[] = [];
    breadcrumbs.push({
      routeParams: ['/', RoutesEnums.GUIDE],
      routeQueryParams: null,
      anchor: RoutesNamesEnum.GUIDE,
    });
    breadcrumbs.push({
      routeParams: null,
      routeQueryParams: null,
      anchor: this.tag()?.name,
    });
    this.breadcrumbs.set(breadcrumbs);
  }

  private updateMetaTags(): void {
    const metaTags: MetaTag[] = [];
    const page: WPTag = this.tag();
    const suffix = ' - Vården.se';

    if (!this.tools.isNil(page)) {
      const title: string = `${page.name} - ${RoutesNamesEnum.GUIDE}`;
      const description: string = page.description;
      this.pageTitle = title + suffix;

      metaTags.push({
        value: title + suffix,
        type: 'title',
        field: 'title',
      });

      metaTags.push({
        value: title + suffix,
        type: 'property',
        field: 'og:title',
      });

      metaTags.push({
        value: 'website',
        type: 'property',
        field: 'og:type',
      });

      metaTags.push({
        value: this.urlBuilder.CreateStaticLink(this.urlBuilder.CreateGuideTagRouterLink(page.slug)),
        type: 'property',
        field: 'og:url',
      });

      metaTags.push({
        value: description,
        type: 'name',
        field: 'description',
      });

      metaTags.push({
        value: description,
        type: 'property',
        field: 'og:description',
      });

      this.metaService.updateMetaTags(metaTags);
    }
  }

  private updateGuides() {
    if (this.page() !== this.loadedPage) {
      const queryId: number = this.loaderService.generateQueueId();
      const queryItem: LoaderQueueItem = {
        id: queryId,
        title: '',
        description: '',
      };
      this.loaderService.addToQueue(queryItem);
      this.subscriptions.push(
        this.guideService
          .GetGuideLatestResponse(this.page(), this.tag().id)
          .pipe(
            finalize(() => {
              this.loaderService.removeFromQueue(queryId);
            }),
          )
          .subscribe(
            (result) => this.processGuideResponse(result),
            (error) => {
              console.error(error);
            },
          ),
      );
    }
  }

  private processGuideResponse(result: HttpResponse<WPPostGuide[]> & {totalPages: number; total: number}) {
    this.loadedPage = this.page();
    this.guidePosts.update((prev) => [...prev, ...result.body]);
    this.totalPages.set(result.totalPages);
    this.isMoreBtnVisible.set(this.page() < this.totalPages());
    this.onPuffsLoaded(result.body);
  }
}
